import { CreateBookingDto } from '../../shared/types';

export interface GetBookingsAPIResponse {
  data: GetBookingsResponse;
}

export interface GetBookingsResponse {
  bookings: Booking[];
  startDate?: string;
  endDate?: string;
}

export interface CreateBookingAPIResponse {
  data: CreateBookingResponse;
}

interface CreateBookingResponse {
  booking: Booking;
}

/*
 * Wire values, spelled exactly as helix spells them (resource-booking/adapters/native/booking/enum/
 * booking.enum.ts). Members are compared against `details.status` straight off the response, so one
 * helix never writes is a comparison that silently never fires. The column is a free-form varchar
 * written by several verticals, so the real list is longer than this and any lookup keyed on it can
 * miss.
 */
export enum BookingStatus {
  PENDING = 'Pending',
  PENDING_PAYMENT = 'Pending_Payment',
  ACCEPTED = 'Accepted',
  CONFIRMED = 'Confirmed',
  DENIED_UNAVAILABLE = 'Denied_Unavailable',
  DENIED_AVAILABLE = 'Denied_Available',
  CANCELED = 'Canceled',
  LATE_CANCEL = 'Late_Canceled',
  NO_SHOW = 'No_Show',
  PAYMENT_CHALLENGE_CANCELED = 'Cancelled_Payment_Challenge',
}

/*
 * Where a booking was made from: `resource_booking_details.made_by`, spelled exactly as helix spells
 * it (same booking.enum.ts). Null on rows that predate the column, and absent from integrations whose
 * details helix stores rather than builds.
 */
export enum BookingMadeBy {
  KIOSK = 'kiosk',
  ADMIN = 'admin',
  WEB = 'web',
  IN_APP = 'in_app',
  MIGRATION = 'migration',
}

/* Made FOR the tenant rather than by them: from admin ("book on behalf of") or at a lobby kiosk. */
const MADE_BY_PROPERTY_TEAM: ReadonlySet<string> = new Set([BookingMadeBy.ADMIN, BookingMadeBy.KIOSK]);

/** Whether the property team made this booking for the tenant. A missing or unknown value is the tenant's own. */
export const isMadeByPropertyTeam = (madeBy?: BookingMadeBy | string | null): boolean =>
  !!madeBy && MADE_BY_PROPERTY_TEAM.has(madeBy);

/**
 * Server status -> the catalogue key naming it for a tenant. Partial: read it through
 * `getStatusLabelId`, since a miss yields `undefined` and `intl.formatMessage` throws on that.
 */
export enum MAP_STATUSES {
  'Pending' = 'pending',
  'Accepted' = 'accepted',
  /* Accepted and paid. Nothing a tenant distinguishes from Accepted, so it shares the word. */
  'Confirmed' = 'accepted',
  'Denied_Unavailable' = 'denied_unavailable',
  'Denied_Available' = 'denied_available',
  'Canceled' = 'canceled',
  'Pending_Payment' = 'pending_payment',
  'Late_Canceled' = 'late_cancel',
  'No_Show' = 'no_show',
  /* Not 'canceled': helix writes this when a 3DS challenge fails and the booking may still be live. */
  'Cancelled_Payment_Challenge' = 'payment_failed',
}

/** The catalogue key for a status, or `undefined` when there is none; callers then render nothing. */
export const getStatusLabelId = (status?: BookingStatus | string): string | undefined =>
  (status && MAP_STATUSES[status as keyof typeof MAP_STATUSES]) || undefined;

export interface Booking {
  name: string;
  id: number | string;
  uuid: string;
  user: {
    first_name: string;
    last_name: string;
    email: string;
    company: {
      name: string;
    };
    resource: string;
  };
  resource: {
    name: string;
  };
  location_name: string;
  email: string;
  userName: string;
  userEmail: string;
  userCompanyName: string;
  resourceName: string;
  start_at: string;
  end_at: string;
  created_at: string;
  reservedDate: string;
  reservedTimeRange: string;
  status: BookingStatus;
  contacted: boolean;
  canceled_at?: string;
  buffer_before?: number;
  buffer_after?: number;
  form_responses?: Array<FormResponse>;
}

export interface BookingsState {
  bookings: Booking[];
  startDate: string;
  endDate: string;
  createdBooking?: Booking;
  loading: boolean;
  processed: boolean;
  cancelProcessed: boolean;
  refreshing: boolean;
  error?: SerializableError;
  createBookingRequest: CreateBookingDto;
}

export enum BookingNotCancelableReasons {
  CANCELATION_PERIOD_CLOSED = 'cancellation_period_closed',
  BOOKING_IN_THE_PAST = 'booking_in_the_past',
}
